import {
  Configuration,
  DefaultApi,
  TrackAvailabilityOptionsDto,
} from '@cratehackers/api-client';
import { config } from '../config';
import { AppIdentifiers } from '../store/slices/token-state.slice';
import { getAppStore } from '../store/storeRef';
import { iTrack } from '../types/ITrack';
import { formatMusicalKey } from '../utils/formatMusicalKey';
import { openApiFetchWrapper } from '../utils/openApiFetchWrapper';

class ApiService {
  private static instance: DefaultApi;

  public static getApiClient(): DefaultApi {
    const apiConfig = new Configuration({
      basePath: config.API_BASE_URL,
      accessToken: ApiService.getAuthToken(),
      fetchApi: openApiFetchWrapper,
    });
    this.instance = new DefaultApi(apiConfig);
    return this.instance;
  }

  public static async getTrackAvailabilityOptions() {
    const apiClient = ApiService.getApiClient();
    const data: TrackAvailabilityOptionsDto =
      await apiClient.trackControllerGetTrackAvailabilityOptions();

    return data;
  }

  public static async getTrackById(trackId: string) {
    try {
      const api = ApiService.getApiClient();
      const track = await api.trackControllerGetTrack({ id: trackId });
      const mappedTrack = ApiService.mapToITrack(track);
      console.log('mappedTrack:', mappedTrack);

      return mappedTrack;
    } catch (error) {
      console.error('Error fetching track by ID:', error);
      return null;
    }
  }

  /**
   * Fetch multiple tracks by their IDs in bulk
   * This is much more efficient for large playlists than fetching individually
   * @param trackIds Array of Spotify track IDs (max 200 per batch, Bubble limit)
   * @returns Map of track ID to hydrated track data
   */
  public static async getBulkTracks(
    trackIds: string[],
  ): Promise<Map<string, any>> {
    const result = new Map<string, any>();

    if (!trackIds || trackIds.length === 0) {
      return result;
    }

    // Process in batches of 200 (Bubble/API limit)
    const BATCH_SIZE = 200;
    for (let i = 0; i < trackIds.length; i += BATCH_SIZE) {
      const batch = trackIds.slice(i, i + BATCH_SIZE);

      try {
        const api = ApiService.getApiClient();
        const data = await api.trackControllerGetBulkTracks({
          bulkTracksRequestDto: { ids: batch },
        });

        if (data.tracks) {
          for (const track of data.tracks) {
            result.set(track.id, track);
          }
        }

        if (data.failed && data.failed.length > 0) {
          console.warn(
            `[ApiService] Failed to fetch ${data.failed.length} tracks`,
          );
        }
      } catch (error) {
        console.error('[ApiService] Error in bulk track fetch:', error);
      }
    }

    return result;
  }

  private static getAuthToken(): string {
    try {
      const appToken = getAppStore().getState().tokenState[AppIdentifiers.App];
      return appToken?.accessToken ?? '';
    } catch {
      return '';
    }
  }

  static readArtistName = (artist: unknown): string => {
    if (typeof artist === 'string') return artist.trim();
    if (artist && typeof artist === 'object' && 'name' in artist) {
      const name = (artist as { name?: unknown }).name;
      return typeof name === 'string' ? name.trim() : '';
    }
    return '';
  };

  private static mapToITrack = (trackSearchDto: any): iTrack => {
    return {
      ID: trackSearchDto.uuid || trackSearchDto.id || '',
      Artist: ApiService.readArtistName(trackSearchDto.artist),
      Title: trackSearchDto.title || trackSearchDto.name || '',
      AlbumCover:
        trackSearchDto.album?.picture ||
        trackSearchDto.album?.imageUrl ||
        trackSearchDto.albumCover,
      BPM: Math.round(trackSearchDto.bpm) || -1,
      energy: Math.round(trackSearchDto.energy * 100) || undefined,
      danceability: Math.round(trackSearchDto.danceability * 100) || undefined,
      valence: Math.round(trackSearchDto.valence * 100) || undefined,
      popularity: trackSearchDto.popularity || undefined,
      Key_Camelot: trackSearchDto.key || '',
      PreviewURL: trackSearchDto.previewUrl || '',
    };
  };

  public static mapBulkTrackToITrack(track: {
    id: string;
    title?: string | null;
    name?: string | null;
    artist?: string | null;
    album?: string | null;
    albumCover?: string | null;
    bpm?: number | null;
    key?: string | null;
    popularity?: number | null;
    energy?: number | null;
    danceability?: number | null;
    valence?: number | null;
    previewUrl?: string | null;
    preview_url?: string | null;
  }): iTrack {
    const key = formatMusicalKey(track.key ?? '');
    const artist =
      typeof track.artist === 'string'
        ? track.artist
        : ApiService.readArtistName(track.artist);
    return {
      ID: track.id,
      SpotifyID: track.id,
      Artist: artist,
      Title: track.title || track.name || '',
      Album: track.album || undefined,
      AlbumCover: track.albumCover || undefined,
      BPM: track.bpm ?? undefined,
      Key_Camelot: key || undefined,
      Key: key || undefined,
      energy: track.energy ?? undefined,
      danceability: track.danceability ?? undefined,
      valence: track.valence ?? undefined,
      popularity: track.popularity ?? undefined,
      PreviewURL: track.previewUrl || track.preview_url || undefined,
    };
  }
}

export default ApiService;
